/**
 * Slice of user settings used for Soniox transcription context (parity with live real-time).
 */
export type SonioxUserSettingsContext = {
  context?: string;
  specialization?: string;
  clinic?: string;
};

/**
 * Payload shape for Soniox REST and WebSocket `context` (general hints + optional background text).
 */
export type SonioxTranscriptionContextPayload = {
  general?: Array<{ key: string; value: string }>;
  text?: string;
};

/**
 * Builds the Soniox `context` object from saved user settings and optional resume text.
 * Keeps async batch transcription aligned with the real-time WebSocket client.
 *
 * @example
 * buildSonioxContextFromUserSettings(
 *   { context: 'NSAID', specialization: 'GP' },
 *   { resumeText: 'Patient presented with...' },
 * );
 */
export function buildSonioxContextFromUserSettings(
  settings: SonioxUserSettingsContext | null | undefined,
  options?: { resumeText?: string },
): SonioxTranscriptionContextPayload | undefined {
  const generalContext = [
    { key: 'words', value: settings?.context?.trim() ?? '' },
    {
      key: 'specialization',
      value: settings?.specialization?.trim() ?? '',
    },
    { key: 'clinic', value: settings?.clinic?.trim() ?? '' },
  ].filter((entry) => entry.value.length > 0);

  const resumeText = options?.resumeText?.trim() ?? '';
  const hasGeneral = generalContext.length > 0;
  const hasText = resumeText.length > 0;

  if (!hasGeneral && !hasText) {
    return undefined;
  }

  return {
    ...(hasGeneral ? { general: generalContext } : {}),
    ...(hasText ? { text: resumeText } : {}),
  };
}
